import React, {useEffect, useRef, useState} from "react";
import {OrderRequestWrap} from "components/atoms/Dashboard/OrderRequest/OrderRequest.styles";
import Foodimage from "assets/Home/Food.png";
import {IoIosArrowDown} from "react-icons/io";
import detail from "assets/Home/detail.svg";
import reject from "assets/Home/reject.svg";
import accept from "assets/Home/accept.svg";
import MenuButton, {MenuItem} from "components/atoms/Menu/Menu";
import {BsThreeDotsVertical} from "react-icons/bs";
import {useAuthContext} from "Context/authContext";
import {useSelector} from "react-redux";
import analyticsService from "services/analyticsService";

const OrderRequest = ({selectedPeriod, customDateRange, currentStoreId}) => {
    const businessId = useSelector(state => state.business.businessId);
    const user = useSelector(state => state.business.user);
    const [selectedOption, setSelectedOption] = useState("Newest");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownOptions = ["Newest", "Oldest"];
    const headerDropdownRef = useRef(null);
    const {hasPermission} = useAuthContext("hasPermission");
    const [recentOrders, setRecentOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalOrders: 0,
        limit: 10,
    });
    const [trendingMenus, setTrendingMenus] = useState([]);
    const [trendingLoading, setTrendingLoading] = useState(false);

    const handleClickOutside = event => {
        if (headerDropdownRef.current && !headerDropdownRef.current.contains(event.target)) {
            setIsDropdownOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleOptionClick = option => {
        setSelectedOption(option);
        setIsDropdownOpen(false);
    };

    const getRecentOrders = async () => {
        try {
            setLoading(true);
            const params = {
                restaurantId: businessId || (Array.isArray(user?.businessId) ? user.businessId[0] : null),
                limit: 10,
                page: 1,
                sortBy: selectedOption.toLowerCase() === "newest" ? "newest" : "oldest",
            };

            // Add storeId if available
            if (currentStoreId) {
                params.storeId = currentStoreId;
            }

            // Add time period filters
            if (selectedPeriod) {
                params.period = selectedPeriod;
            }

            // Add custom date range if selected
            if (selectedPeriod === "custom") {
                if (customDateRange.startDate) {
                    params.startDate = customDateRange.startDate;
                }
                if (customDateRange.endDate) {
                    params.endDate = customDateRange.endDate;
                }
            }

            const res = await analyticsService.getRecentOrders(params);
            console.log("📦 OrderRequest: API response:", res);

            if (res?.success && res?.data) {
                setRecentOrders(res.data.orders || []);
                setPagination(
                    res.data.pagination || {
                        currentPage: 1,
                        totalPages: 1,
                        totalOrders: 0,
                        limit: 10,
                    }
                );
            }
        } catch (error) {
            console.error("📦 OrderRequest: Error fetching recent orders:", error);
            setRecentOrders([]);
        } finally {
            setLoading(false);
        }
    };

    const getTrendingMenus = async () => {
        try {
            setTrendingLoading(true);
            const params = {
                restaurantId: businessId || (Array.isArray(user?.businessId) ? user.businessId[0] : null),
                limit: 10,
            };

            // Add storeId if available
            if (currentStoreId) {
                params.storeId = currentStoreId;
            }

            // Add time period filters
            if (selectedPeriod) {
                params.period = selectedPeriod;
            }

            // Add custom date range if selected
            if (selectedPeriod === "custom") {
                if (customDateRange.startDate) {
                    params.startDate = customDateRange.startDate;
                }
                if (customDateRange.endDate) {
                    params.endDate = customDateRange.endDate;
                }
            }

            const res = await analyticsService.getTrendingMenus(params);
            if (res?.success && res?.data) {
                setTrendingMenus(res.data.trendingMenus || []);
            }
        } catch (error) {
            console.error("🔥 TrendingMenus: Error fetching trending menus:", error);
            setTrendingMenus([]);
        } finally {
            setTrendingLoading(false);
        }
    };

    useEffect(() => {
        if (currentStoreId && (businessId || user?.businessId?.[0])) {
            getRecentOrders();
            getTrendingMenus();
        }
    }, [currentStoreId, selectedPeriod, customDateRange, selectedOption]);

    // Helper function to format order status for display
    const formatStatus = status => {
        const statusMap = {
            new_order: "Pending",
            accepted: "Accepted by Rider",
            preparing: "Order Preparing",
            ready_for_pickup: "Ready for Pickup",
            out_for_delivery: "Out For Delivery",
            delivered: "Delivered",
            cancelled: "Cancelled",
        };
        return statusMap[status] || status;
    };

    return (
        <OrderRequestWrap>
            <div className="orderRequest">
                <div className="parentRecentOrder">
                    <div className="HeadingWrap">
                        <div className="headMenu">
                            <h2 className="heading">Recent Order Request</h2>
                            <p>{loading ? "Loading..." : `${pagination.totalOrders} orders`}</p>
                        </div>
                        <div className="order-dropdown" ref={headerDropdownRef}>
                            <div className="dropdown" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                                <p>{selectedOption}</p>
                                <IoIosArrowDown style={{color: "grey"}} />
                            </div>
                            {isDropdownOpen && (
                                <ul className="styled-select">
                                    {dropdownOptions.map(option => (
                                        <li key={option} onClick={() => handleOptionClick(option)}>
                                            {option}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    <div>
                        <div className="orderHolder">
                            {loading ? (
                                <div style={{textAlign: "center", padding: "20px", color: "#999"}}>
                                    Loading orders...
                                </div>
                            ) : recentOrders.length === 0 ? (
                                <div style={{textAlign: "center", padding: "20px", color: "#999"}}>
                                    No recent orders found for the selected period
                                </div>
                            ) : (
                                recentOrders.map((order, index) => {
                                    const firstItem = order.items && order.items.length > 0 ? order.items[0] : null;
                                    const totalItems = order.items
                                        ? order.items.reduce((sum, item) => sum + (item.quantity || 0), 0)
                                        : 0;
                                    const address = order.deliveryAddress
                                        ? `${order.deliveryAddress.street_address || ""}, ${order.deliveryAddress.city || ""}`
                                        : "No address provided";

                                    return (
                                        <div className="orderRow" key={order.orderId || index}>
                                            <div className="orderContent">
                                                <div className="orderImage">
                                                    <img
                                                        src={firstItem?.image || Foodimage}
                                                        alt={firstItem?.name || "Order item"}
                                                        onError={e => {
                                                            e.target.src = Foodimage;
                                                        }}
                                                    />
                                                </div>
                                                <div className="contentHolder">
                                                    <div className="content">
                                                        <div className="orderDescription">
                                                            <strong>{firstItem?.name || "Order items"} </strong>
                                                            <span>
                                                                {order.trackingId || `#${order.orderId?.slice(-8)}`}
                                                            </span>
                                                        </div>
                                                        <div className="orderName">
                                                            <strong>{order?.customerName || "Customer"}</strong>
                                                            {/* <span>{address}</span> */}
                                                            <span>{order?.customerEmail}</span>
                                                        </div>
                                                    </div>
                                                    <div className="content">
                                                        <div className="countingWrapper">
                                                            <span className="counting">
                                                                ${order.totalPrice?.toFixed(2) || "0.00"}
                                                                <span className="quantity"> x{totalItems}</span>
                                                            </span>
                                                        </div>
                                                        <strong className={`status ${order.status?.toLowerCase()}`}>
                                                            {formatStatus(order.status)}
                                                        </strong>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* <div className="items">
                        <MenuButton
                          transition
                          icon={
                            <BsThreeDotsVertical size={20} className="list-icon" />
                          }
                        >
                          {hasPermission("live-orders.accept") && (
                            <MenuItem className="accept">
                              <img src={accept} alt="accept" /> Accept Order
                            </MenuItem>
                          )}
                          {hasPermission("live-orders.reject") && (
                            <MenuItem className="reject">
                              <img src={reject} alt="reject" /> Reject Order
                            </MenuItem>
                          )}
                          <MenuItem className="detail">
                            <img src={detail} alt="detail" /> View Details
                          </MenuItem>
                        </MenuButton>
                      </div> */}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="trending">
                <div className="trendingMenus">
                    <div className="headMenu">
                        <h2 className="heading">Trending Menus</h2>
                        <p>{trendingLoading ? "Loading..." : `${trendingMenus.length} trending items`}</p>
                    </div>
                    {trendingLoading ? (
                        <div style={{textAlign: "center", padding: "20px", color: "#999"}}>
                            Loading trending menus...
                        </div>
                    ) : trendingMenus.length === 0 ? (
                        <div style={{textAlign: "center", padding: "20px", color: "#999"}}>
                            No trending menus found for the selected period
                        </div>
                    ) : (
                        trendingMenus.map((menu, index) => (
                            <div className="menuSetting borderBottom" key={menu._id || index}>
                                <div className="trendingImgWrap">
                                    <div className="imgWrap">
                                        <img
                                            src={menu.image || Foodimage}
                                            alt={menu.name || "Menu item"}
                                            onError={e => {
                                                e.target.src = Foodimage;
                                            }}
                                        />
                                    </div>
                                    <span className="imageText">#{index + 1}</span>
                                </div>
                                <div className="textContainer">
                                    <div className="itemName">{menu.name}</div>
                                    <div className="menuPrice">
                                        <div className="itemPrice">${menu.price?.toFixed(2) || "0.00"}</div>
                                        <div className="itemOrder">Orders {menu.totalOrders}x</div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </OrderRequestWrap>
    );
};
export default OrderRequest;
